import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    GripVertical, CheckSquare, Bell, MessageSquare, Flag,
    Clock, Users, X, FileText, Brain, BookOpen
} from 'lucide-react';

const TimelineItem = ({ item, viewMode, onRemove, onTimeChange, isDragging = false }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSorting,
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    // Handler for time change
    const handleTimeChange = (e) => {
        e.stopPropagation(); // Prevent drag start
        if (onTimeChange) {
            onTimeChange(item.id, e.target.value);
        }
    };

    // Get type-specific styles
    const getTypeStyles = () => {
        const type = item.item_type;
        const taskType = item.task_type;

        if (type === 'notice') {
            return {
                bg: 'bg-yellow-50',
                border: 'border-yellow-300',
                iconBg: 'bg-yellow-100',
                iconColor: 'text-yellow-600',
                icon: Bell,
                label: 'Notice'
            };
        }

        if (type === 'survey') {
            return {
                bg: 'bg-green-50',
                border: 'border-green-300',
                iconBg: 'bg-green-100',
                iconColor: 'text-green-600',
                icon: MessageSquare,
                label: 'Survey'
            };
        }

        if (type === 'milestone') {
            return {
                bg: 'bg-purple-50',
                border: 'border-purple-300',
                iconBg: 'bg-purple-100',
                iconColor: 'text-purple-600',
                icon: Flag,
                label: 'Milestone'
            };
        }

        // Task types
        if (type === 'task') {
            if (taskType === 'quiz') {
                return {
                    bg: 'bg-blue-50',
                    border: 'border-blue-300',
                    iconBg: 'bg-blue-100',
                    iconColor: 'text-blue-600',
                    icon: Brain,
                    label: 'Quiz'
                };
            }
            if (taskType === 'training') {
                return {
                    bg: 'bg-blue-50',
                    border: 'border-blue-300',
                    iconBg: 'bg-blue-100',
                    iconColor: 'text-blue-600',
                    icon: BookOpen,
                    label: 'Training'
                };
            }
            if (taskType === 'checklist') {
                return {
                    bg: 'bg-blue-50',
                    border: 'border-blue-300',
                    iconBg: 'bg-blue-100',
                    iconColor: 'text-blue-600',
                    icon: CheckSquare,
                    label: 'Checklist'
                };
            }
            return {
                bg: 'bg-blue-50',
                border: 'border-blue-300',
                iconBg: 'bg-blue-100',
                iconColor: 'text-blue-600',
                icon: FileText,
                label: 'Task'
            };
        }

        // Default
        return {
            bg: 'bg-gray-50',
            border: 'border-gray-300',
            iconBg: 'bg-gray-100',
            iconColor: 'text-gray-600',
            icon: FileText,
            label: 'Item'
        };
    };

    const typeStyles = getTypeStyles();
    const Icon = typeStyles.icon;

    // Compact View
    if (viewMode === 'compact') {
        return (
            <div
                ref={setNodeRef}
                style={style}
                className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-lg border
                    ${typeStyles.bg} ${typeStyles.border}
                    ${isDragging || isSorting ? 'opacity-50 shadow-lg' : ''}
                    group
                `}
            >
                <button
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                >
                    <GripVertical className="w-3 h-3" />
                </button>

                <Icon className={`w-3.5 h-3.5 ${typeStyles.iconColor}`} />

                <span className="flex-1 text-sm text-gray-800 truncate">
                    {item.item_title || 'Untitled'}
                </span>

                <div
                    className="flex items-center gap-1 text-gray-500 bg-white/50 px-1.5 py-0.5 rounded"
                    onPointerDown={(e) => e.stopPropagation()} // Stop drag interaction
                >
                    <Clock className="w-3 h-3" />
                    <input
                        type="time"
                        value={item.release_time || ''}
                        onChange={handleTimeChange}
                        className="bg-transparent text-xs border-none p-0 focus:ring-0 w-[55px]"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>

                {onRemove && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove();
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                    >
                        <X className="w-3 h-3" />
                    </button>
                )}
            </div>
        );
    }

    // Detailed View
    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`
                flex items-start gap-3 p-3 rounded-xl border-2
                ${typeStyles.bg} ${typeStyles.border}
                ${isDragging || isSorting ? 'opacity-50 shadow-lg' : ''}
                group
            `}
        >
            <button
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 mt-1"
            >
                <GripVertical className="w-4 h-4" />
            </button>

            <div className={`p-2 rounded-lg ${typeStyles.iconBg}`}>
                <Icon className={`w-5 h-5 ${typeStyles.iconColor}`} />
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <h4 className="font-semibold text-gray-800 text-sm">
                            {item.item_title || 'Untitled'}
                        </h4>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeStyles.iconBg} ${typeStyles.iconColor}`}>
                                {typeStyles.label}
                            </span>
                            <span
                                className="flex items-center gap-1 text-xs text-gray-500 bg-white/50 px-2 py-0.5 rounded border border-gray-200"
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                <Clock className="w-3 h-3" />
                                <input
                                    type="time"
                                    value={item.release_time || ''}
                                    onChange={handleTimeChange}
                                    className="bg-transparent text-xs border-none p-0 focus:ring-0 w-[60px]"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </span>
                            {item.assignment_placeholders && item.assignment_placeholders.length > 0 && (
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                    <Users className="w-3 h-3" />
                                    {item.assignment_placeholders.join(', ')}
                                </span>
                            )}
                        </div>
                    </div>

                    {onRemove && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove();
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TimelineItem;
